import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GraduationCap, ChevronDown, ExternalLink, Mail } from "lucide-react";

const TRAINING_URL = "https://training.karmsafety.com";
const TRAINING_CONTACT_EMAIL = "shane@karmsafety.com";
const TRAINING_CONTACT_SUBJECT = "KARM Safety — Group Training Inquiry";
const TRAINING_CONTACT_BODY =
  "Hi KARM Safety team,\n\nI'd like to schedule training for a larger group. Details:\n\n• Number of attendees:\n• Preferred dates:\n• Location:\n\n— Sent from the KARM Safety App";

function buildMailtoHref() {
  const params = new URLSearchParams({
    subject: TRAINING_CONTACT_SUBJECT,
    body: TRAINING_CONTACT_BODY,
  });
  return `mailto:${TRAINING_CONTACT_EMAIL}?${params.toString()}`;
}

/**
 * Ghost "Training" button with a dropdown menu.
 * Sized to match `btn-primary` so it sits flush next to the New Employee button.
 *
 * Props:
 *   size: "lg" (matches btn-primary) | "md" (matches header chips)
 */
export default function TrainingMenu({ size = "lg" }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const positionMenu = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuWidth = 280;
    // Anchor menu under the button, right-aligned so it doesn't blow off-screen on the right.
    const left = Math.min(r.left, window.innerWidth - menuWidth - 8);
    setCoords({ top: r.bottom + 6, left: Math.max(8, left) });
  };

  useEffect(() => {
    if (!open) return;
    positionMenu();
    const onScroll = () => positionMenu();
    const onClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-training-menu]")) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openContact = (e) => {
    // Programmatic mailto navigation — same pattern as the support button.
    e.preventDefault();
    setOpen(false);
    try {
      window.location.href = buildMailtoHref();
    } catch {
      window.open(buildMailtoHref(), "_blank");
    }
  };

  const heightCls = size === "md" ? "h-11 px-4 text-xs" : "h-12 px-5 text-sm md:h-14 md:px-6 md:text-base";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid="training-menu-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${heightCls} inline-flex items-center justify-center gap-2 bg-transparent border-2 border-safety-yellow text-safety-yellow font-black uppercase tracking-wider hover:bg-safety-yellow hover:text-black transition-colors active:translate-x-[1px] active:translate-y-[1px]`}
      >
        <GraduationCap className="w-5 h-5" strokeWidth={3} />
        Training
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          strokeWidth={3}
        />
      </button>

      {open && createPortal(
        <div
          data-training-menu
          data-testid="training-menu"
          role="menu"
          className="fixed z-[70] w-[280px] border-2 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-150"
          style={{
            top: coords.top,
            left: coords.left,
            backgroundColor: "#0B1E3F",
            borderColor: "#F9D71C",
            color: "#fff",
          }}
        >
          <MenuItem
            testId="menu-schedule-training"
            href={TRAINING_URL}
            external
            Icon={ExternalLink}
            label="Schedule Training Online"
            sub="Opens the KARM training portal"
            onClick={() => setOpen(false)}
          />
          <MenuItem
            testId="menu-contact-group"
            href={buildMailtoHref()}
            Icon={Mail}
            label="Contact Us for larger group"
            sub={TRAINING_CONTACT_EMAIL}
            onClick={openContact}
            divider
          />
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuItem({ Icon, label, sub, href, external, onClick, testId, divider }) {
  return (
    <a
      role="menuitem"
      data-testid={testId}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onClick={onClick}
      className={`w-full px-4 py-3 inline-flex items-start gap-3 hover:bg-safety-yellow hover:text-black transition-colors text-left ${
        divider ? "border-t-2" : ""
      }`}
      style={divider ? { borderTopColor: "rgba(249,215,28,0.3)" } : undefined}
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.75} />
      <div className="min-w-0">
        <div className="text-sm font-bold uppercase tracking-wider leading-tight">{label}</div>
        <div className="text-[11px] mt-0.5 opacity-70 truncate">{sub}</div>
      </div>
    </a>
  );
}
